/**
 * C11 (البوليصة والعقد — policy & contract, read-only) view-model — pure display
 * shaping kept out of the .vue so node:test can cover it directly (the companies
 * repo has no SFC test runner). Resolves a policy's coverageIds to localized
 * coverage names, picks the selected policy from the chip bar, and maps a
 * bilingual entity to the active locale's name. Plain CommonJS.
 */
var POLICY_STATUS = {
  active: { tag: 'ok', key: 'statusActive' },
  expiring: { tag: 'review', key: 'statusExpiring' },
  expired: { tag: 'danger', key: 'statusExpired' },
};

/** nameAr under 'ar'; nameEn (falling back to nameAr) under 'en'. Empty-safe. */
function localizedName(entity, locale) {
  if (!entity) return '';
  if (locale === 'en') return entity.nameEn || entity.nameAr || '';
  return entity.nameAr || entity.nameEn || '';
}

/**
 * Map a policy's coverageIds to { id, name } using the coverages lookup list.
 * Unknown ids (or an unavailable coverages list) yield name === null so the view
 * can degrade gracefully. Empty-safe on both arguments.
 */
function resolveCoverages(coverageIds, coverages, locale) {
  var ids = coverageIds || [];
  var byId = {};
  (coverages || []).forEach(function (c) {
    byId[c.id] = c;
  });
  return ids.map(function (id) {
    var c = byId[id];
    return { id: id, name: c ? localizedName(c, locale) : null };
  });
}

/** The policy matching selectedId, else the first policy, else null. */
function selectPolicy(policies, selectedId) {
  var rows = policies || [];
  if (rows.length === 0) return null;
  if (selectedId != null) {
    for (var i = 0; i < rows.length; i++) {
      if (String(rows[i].id) === String(selectedId)) return rows[i];
    }
  }
  return rows[0];
}

/** Tag class + i18n key for a policy status, with a muted fallback. */
function statusMeta(status) {
  return POLICY_STATUS[status] || { tag: 'muted', key: 'statusUnknown' };
}

/** Preorder-flatten the contract tree into depth-tagged rows (depth-agnostic —
 *  recurses on `children` with no assumption of a fixed number of levels). */
function flattenTree(entities, depth) {
  var d = depth || 0;
  var out = [];
  (entities || []).forEach(function (node) {
    out.push({
      id: node.id, kind: node.kind, nameAr: node.nameAr, nameEn: node.nameEn,
      depth: d, isActive: node.isActive !== false, benefits: node.benefits || [],
    });
    if (node.children && node.children.length) {
      out = out.concat(flattenTree(node.children, d + 1));
    }
  });
  return out;
}

/**
 * Null-safe shaping for a benefit's limit display. A benefit can be UNCAPPED
 * (limitAmount === null, per the backend's nullable typing + the admin
 * editor's optional limits) and/or have no limitPeriod set — the view must
 * not render fMoney(null) ("EGP 0", which reads as "not covered" in an
 * insurance context) nor a dangling " / " when the period is absent.
 */
function benefitLimitParts(benefit) {
  var hasAmount = !!(benefit && benefit.limitAmount != null);
  var hasPeriod = !!(benefit && benefit.limitPeriod != null);
  return {
    hasAmount: hasAmount,
    amount: hasAmount ? benefit.limitAmount : null,
    hasPeriod: hasPeriod,
    periodValue: hasPeriod ? benefit.limitPeriod : null,
  };
}

module.exports = {
  POLICY_STATUS: POLICY_STATUS,
  localizedName: localizedName,
  resolveCoverages: resolveCoverages,
  selectPolicy: selectPolicy,
  statusMeta: statusMeta,
  flattenTree: flattenTree,
  benefitLimitParts: benefitLimitParts,
};
